import * as React from "react";
import {
  Grid2 as Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  TextField,
  DialogContentText,
  Tooltip,
  IconButton,
} from "@mui/material";
import { connect } from "react-redux";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import "froala-editor/js/plugins/colors.min.js";

import {
  Theme,
  createStyles,
} from "@mui/material/styles";
import type { WithStyles } from "@mui/styles";
import { withStyles } from "@mui/styles";
import {
  IGenericUserProps,
  genericMapStateToProps,
} from "../../DataClasses/GenericUser";
import idssStyle from "../../Configuration/SharedStyling";
import { IAnnouncementPayload } from "../../DataClasses/AnnouncementData";
import SharedMaterialReactTable, { ISharedTableColumn } from "../Shared/MaterialReactTable";
import AnnouncementsService from "../Services/AnnouncementsService";
import { AjaxError } from "rxjs/ajax";
import { Delete, Edit } from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  LocalizationProvider,
  DatePicker,
  DateTimePicker,
} from "@mui/x-date-pickers";
import froalaBaseConfig from "../../Utilities/BaseFroalaConfig";
import FroalaEditor from "react-froala-wysiwyg";
import SharedUtility from "../../Utilities/SharedUtility";
import { Redirect } from "react-router-dom";
import { format, isValid, parse } from 'date-fns';
import { firstValueFrom } from 'rxjs';


const styles = (theme: Theme) => createStyles({ ...idssStyle(theme) });

interface IAnnouncementsListStateStore {
  payload: IAnnouncementPayload[];
  isLoading: boolean;
  openEditDialog: boolean;
  openDeleteDialog: boolean;
  editingItemId: number;
  editingStartDate: string;
  editingStopDate: string;
  editingDeadlineDate: string;
  editingDisplayOrder: string;
  fConfig: any;
  fModel: string;
  redirect: boolean;
}

type IStylesProps = WithStyles<typeof styles>;

class AnnouncementsListView extends React.Component<
  IGenericUserProps & IStylesProps,
  IAnnouncementsListStateStore
> {
  constructor(props: any) {
    super(props);

    let config: any = {
      toolbarButtons: [
        "bold",
        "textColor",
        "italic",
        "underline",
        "insertLink",
        "undo",
        "redo",
      ],
      attribution: false,
    };
    this.state = {
      payload: [],
      isLoading: true,
      openEditDialog: false,
      openDeleteDialog: false,
      editingItemId: -1,
      editingStartDate: "",
      editingStopDate: "",
      editingDeadlineDate: "",
      editingDisplayOrder: "-1",
      fConfig: config,
      fModel: "",
      redirect: false
    };
  }

  public componentDidMount() {
    this.RefreshModel();
  }

  private redirectToErrorPage() {
    return <Redirect to='/errorpage' />;
  }

  private setRedirectState() {
    this.setState({ redirect: true });
  }

  // Define table columns
  private getTableColumns = (): ISharedTableColumn<any>[] => [
    {
      accessorKey: 'id',
      header: 'Id',
      size: 80,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'text',
      header: 'Text',
      enableSorting: false,
      enableFiltering: true,
      Cell: ({ cell }) => (
        <FroalaEditorView model={cell.getValue()} config={{ ...froalaBaseConfig }} />
      ),
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      size: 120,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'stopDate',
      header: 'Stop Date',
      size: 120,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'displayOrder',
      header: 'Display Order',
      size: 120,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'createUser',
      header: 'Create User',
      size: 150,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'createDate',
      header: 'Create Date',
      size: 120,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 120,
      enableSorting: false,
      enableFiltering: false,
      enableColumnActions: false,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Edit Announcement" aria-label="editAnnouncement">
            <IconButton
              size="small"
              aria-label="editAnnouncement"
              onClick={() => this.editDialogOpen(row.original.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Announcement" aria-label="deleteAnnouncement">
            <IconButton
              size="small"
              aria-label="deleteAnnouncement"
              onClick={() => this.deleteDialogOpen(row.original.id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  public render() {
    if (this.state.redirect) {
      return this.redirectToErrorPage();
    }
    const { classes } = this.props;

    const tableData = this.state.payload.map((item) => ({
      id: item.id,
      text: item.text,
      startDate: SharedUtility.ToLocaleDateString$(item.startDate),
      stopDate: SharedUtility.ToLocaleDateString$(item.stopDate),
      displayOrder: item.displayOrder,
      createUser: item.lastChangedBy,
      createDate: SharedUtility.ToLocaleDateString$(item.lastChangedOn),
    }));

    return (
      <Grid container={true} className={classes.gridRowFlex} spacing={2}>
        <Grid size={12}>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={this.addNewAnnounementBtnClicked.bind(this)}
              className={classes.mb2}
            >
              Add New Announcement
            </Button>

            <SharedMaterialReactTable
              data={tableData}
              columns={this.getTableColumns()}
              title="Manage Announcement"
              isLoading={this.state.isLoading}
              enablePagination={true}
              enableSorting={true}
              enableFiltering={true}
              enableColumnActions={true}
              enableRowActions={false}
              initialState={{
                pagination: { pageIndex: 0, pageSize: 15 },
              }}
              muiPaginationProps={{
                rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
              }}
              emptyMessage="No announcements found."
            />
          </div>
          <Dialog open={this.state.openEditDialog}>
            <DialogTitle id="max-width-dialog-title">
              Add/Edit Announcement <i>(ID: {this.state.editingItemId})</i> 
            </DialogTitle>
            <DialogContent>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <Grid container justifyContent="space-around" spacing={2}>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField
                            variant="outlined"
                            label="Display Order"
                            value={this.state.editingDisplayOrder}
                            onChange={(e) => this.handleDisplayOrderChange(e)}
                            fullWidth
                            margin="normal"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          {this.isDeadlineRequired() && (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DateTimePicker
                                label="Deadline Date & Time"
                                value={this.parseDeadlineDate()}
                                onChange={(newValue) => this.handleDeadlineDateChange(newValue)}
                                format="yyyy-MM-dd HH:mm"
                                ampm={false}
                                slotProps={{
                                  textField: {
                                    margin: "normal",
                                    fullWidth: true,
                                    error: this.isDeadlineDateInvalid(),
                                    helperText: this.isDeadlineDateInvalid() ? "Invalid Date Format" : ""
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          )}
                        </Grid>
                      </Grid>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <br />
                      <div>
                        <i>
                          <small>
                            * The Start/Stop Dates are not required. Leave the
                            field(s) empty if you don't want it.
                          </small>
                        </i>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container justifyContent="space-around" spacing={2}>
                          <Grid size={{ xs: 12, sm: 5 }}>
                            <DatePicker
                              label="Start Date"
                              format="yyyy-MM-dd"
                              value={this.state.editingStartDate ? new Date(this.state.editingStartDate) : null}
                              onChange={(newValue) => this.handleStartDateChange(newValue)}
                              slotProps={{
                                textField: {
                                  margin: "normal",
                                  fullWidth: true
                                }
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 5 }}>
                            <DatePicker
                              label="Stop Date"
                              format="yyyy-MM-dd"
                              value={this.state.editingStopDate ? new Date(this.state.editingStopDate) : null}
                              onChange={(newValue) => this.handleEndDateChange(newValue)}
                              slotProps={{
                                textField: {
                                  margin: "normal",
                                  fullWidth: true
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </LocalizationProvider>
                    </td>
                  </tr>
                  <tr>
                    <td>Text:</td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <i>
                          <small>
                            * Instances of &#123;0&#125; will be replaced by the number of days until the Deadline Date. Instances of &#123;1&#125; will be replaced by the Deadline Date.
                          </small>
                        </i>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FroalaEditor
                        config={{ ...froalaBaseConfig, ...this.state.fConfig }}
                        model={this.state.fModel}
                        onModelChange={(e) => this.setState({ fModel: e })}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={
                  this.state.fModel === "" ||
                  (!(
                    this.state.editingStartDate === "" ||
                    isNaN(Date.parse(this.state.editingStartDate)) ||
                    this.state.editingStopDate === "" ||
                    isNaN(Date.parse(this.state.editingStopDate))
                  ) &&
                    new Date(this.state.editingStartDate) >
                    new Date(this.state.editingStopDate))
                }
                onClick={this.handleEditDialogSave.bind(this)}
              >
                Save
              </Button>
              <Button
                className={classes.disabledButton}
                onClick={this.handleEditDialogClose.bind(this)}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={this.state.openDeleteDialog}
            onClose={this.deleteDialogClose.bind(this)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Are you sure you want to delete this announcement?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                The item will be removed from announcements list.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleDeleteAnnouncement.bind(this)}
                color="primary"
                autoFocus
              >
                Yes
              </Button>
              <Button
                onClick={this.deleteDialogClose.bind(this)}
                color="primary"
              >
                No
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    );
  }

  private isDeadlineRequired() {
    return this.state.fModel.includes("{1}");
  }

  private parseDeadlineDate() {
    if (!this.state.editingDeadlineDate) {
      return null;
    }
    
    // Try parsing as ISO string first (from server)
    const isoDate = new Date(this.state.editingDeadlineDate);
    if (isValid(isoDate)) {
      return isoDate;
    }
    
    // Try parsing as custom format
    const parsedDate = parse(this.state.editingDeadlineDate, "yyyy-MM-dd HH:mm", new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    }
    
    return null;
  }

  private isDeadlineDateInvalid() {
    return this.state.editingDeadlineDate && !this.parseDeadlineDate();
  }

  private deleteDialogClose() {
    this.setState({ openDeleteDialog: false, editingItemId: -1 });
  }
  
  private async handleDeleteAnnouncement() {
    await firstValueFrom(
      AnnouncementsService.DeleteAnnouncement$(
        this.props.user,
        this.state.editingItemId
      )
    );
    await this.RefreshModel();
    this.deleteDialogClose();
  }
  
  private async handleEditDialogSave() {
    await firstValueFrom(
      AnnouncementsService.SaveAnnoucement$(
        this.props.user,
        this.state.editingItemId,
        this.state.editingDisplayOrder,
        this.state.editingStartDate,
        this.state.editingStopDate,
        this.state.editingDeadlineDate,
        this.state.fModel
      )
    );
    await this.RefreshModel();
    this.handleEditDialogClose();
  }
  
  private handleEditDialogClose() {
    this.setState({
      openEditDialog: false,
      editingDisplayOrder: "-1",
      editingStartDate: "",
      editingStopDate: "",
      editingDeadlineDate: "",
      fModel: "",
      editingItemId: -1,
    });
  }
  
  private handleDisplayOrderChange(e) {
    if (Number(e.target.value) && !e.target.value.includes("."))
      this.setState({ editingDisplayOrder: e.target.value });
  }

  private handleStartDateChange(date) {
    if (date && isValid(date)) {
      this.setState({ editingStartDate: date.toISOString() });
    } else {
      this.setState({ editingStartDate: "" });
    }
  }

  private handleEndDateChange(date) {
    if (date && isValid(date)) {
      this.setState({ editingStopDate: date.toISOString() });
    } else {
      this.setState({ editingStopDate: "" });
    }
  }

  private handleDeadlineDateChange = (date) => {
    if (date && isValid(date)) {
      const formatted = format(date, "yyyy-MM-dd HH:mm");
      this.setState({ editingDeadlineDate: formatted });
    } else {
      this.setState({ editingDeadlineDate: "" });
    }
  };

  private editDialogOpen(value: any) {
    console.log(this.state.payload);
    var item = this.state.payload.filter((x) => x.id.toString() === value.toString())[0];
    console.log("found item");
    console.log(item);

    this.setState({
      editingItemId: item.id,
      editingStartDate: item.startDate ? new Date(item.startDate).toISOString() : "",
      editingStopDate: item.stopDate ? new Date(item.stopDate).toISOString() : "",
      editingDeadlineDate: item.deadlineDate ? this.formatDeadlineForDisplay(item.deadlineDate) : "",
      editingDisplayOrder: item.displayOrder.toString(),
      fModel: item.text,
      openEditDialog: true,
    });
  }

  private formatDeadlineForDisplay(deadlineDate: any): string {
    if (!deadlineDate) return "";
    
    const date = new Date(deadlineDate);
    if (isValid(date)) {
      return format(date, "yyyy-MM-dd HH:mm");
    }
    return "";
  }
  
  private deleteDialogOpen(value) {
    this.setState({ editingItemId: value, openDeleteDialog: true });
  }
  
  private addNewAnnounementBtnClicked() {
    this.setState({
      editingItemId: -1,
      editingStartDate: "",
      editingStopDate: "",
      editingDeadlineDate: "",
      editingDisplayOrder: "-1",
      fModel: "",
      openEditDialog: true,
    });
  }

  private async RefreshModel() {
    this.setState({ isLoading: true });
    const retval = await firstValueFrom(
      AnnouncementsService.GetAnnouncementsDetail$(this.props.user)
    );
    if (retval instanceof AjaxError || !retval) {
      this.setRedirectState();
    } else {
      this.setState({ payload: retval });
    }
    this.setState({ isLoading: false });
  }
}

// REDUX CONTAINER
const AnnouncementsListPageWithState = connect<IGenericUserProps>(
  genericMapStateToProps
)(AnnouncementsListView);

// STYLES CONTAINER
const AnnouncementsListPage = withStyles(styles)(
  AnnouncementsListPageWithState
);

export default AnnouncementsListPage;
